import React, {useCallback, useContext, useMemo, useState} from 'react';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {createShowSnackbar} from '@/adjustment/adjust/dialogs/utils/working-helpers';

/**
 * Parent (FsAdjustment) may still receive snacks via DialogLauncher onSnackbar / context.
 * After Save, WP dialogs stay open (`closeOnSuccess: false`) — save API results must show
 * on the WP's local SnackbarField. Validation / navigation messages also use the local field.
 */
export const WpSnackbarContext = React.createContext<ShowSnackbarFn | null>(null);

export function useWpSnackbarContext(): ShowSnackbarFn | null {
    return useContext(WpSnackbarContext);
}

/** Prefer explicit prop, then payload, then FsAdjustment context. */
export function useWpShowSnackbar(
    onSnackbar?: ShowSnackbarFn | null,
    payloadOnSnackbar?: ShowSnackbarFn | null,
): ShowSnackbarFn {
    const ctx = useWpSnackbarContext();
    return useCallback(
        (message: string, severity: 'warning' | 'error' | 'success') => {
            const fn = onSnackbar ?? payloadOnSnackbar ?? ctx;
            fn?.(message, severity);
        },
        [onSnackbar, payloadOnSnackbar, ctx],
    );
}

export type WpDialogSnackbar = {
    /** Local in-dialog snackbar (validation, navigation, adjustment prep, save result, etc.) */
    showSnackbar: ShowSnackbarFn;
    /**
     * Post–saveOrUpdate (or equivalent WP save API) result.
     * Always shows on the local SnackbarField so feedback is visible while the WP stays open.
     */
    notifySaveResult: ShowSnackbarFn;
    snackbarFieldProps: {
        open: boolean;
        message: string;
        severity: 'warning' | 'error' | 'success';
        autoHideDuration: number;
        onClose: () => void;
    };
};

/**
 * Dual-channel snackbar for paper-registry WP dialogs:
 * - showSnackbar → local SnackbarField (validation, navigation, etc.)
 * - notifySaveResult → local SnackbarField (save success/error while dialog stays open)
 *
 * Parent queue (DialogLauncher → FsAdjustment) is no longer used for save results:
 * Save keeps the WP mounted (`closeOnSuccess: false`), so in-component feedback is the correct UX.
 *
 * `onSnackbar` / `payloadOnSnackbar` remain in the signature for call-site compatibility
 * (DialogLauncher still passes them); save results intentionally ignore the parent channel.
 */
export function useWpDialogSnackbar(
    _onSnackbar?: ShowSnackbarFn | null,
    _payloadOnSnackbar?: ShowSnackbarFn | null,
): WpDialogSnackbar {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'warning' | 'error' | 'success'>('warning');

    const showSnackbar = useMemo(
        () => createShowSnackbar(setSnackbarMessage, setSnackbarSeverity, setOpenSnackbar),
        [],
    );

    const notifySaveResult = useCallback<ShowSnackbarFn>(
        (message, severity) => {
            showSnackbar(message, severity);
        },
        [showSnackbar],
    );

    return {
        showSnackbar,
        notifySaveResult,
        snackbarFieldProps: {
            open: openSnackbar,
            message: snackbarMessage,
            severity: snackbarSeverity,
            autoHideDuration: 4000,
            onClose: () => setOpenSnackbar(false),
        },
    };
}
